import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Field validation logic
  const validateField = (name, value) => {
    let error = '';
    const trimmed = (value || '').trim();

    if (name === 'name') {
      if (!trimmed) {
        error = 'Full name is required';
      } else if (trimmed.length < 3) {
        error = 'Name must be at least 3 characters';
      } else if (!/^[a-zA-Z\s.]+$/.test(trimmed)) {
        error = 'Only letters, spaces, and dots allowed';
      }
    } else if (name === 'phone') {
      const cleanPhone = trimmed.replace(/[\s\-()]/g, '');
      if (!trimmed) {
        error = 'Phone number is required';
      } else if (!/^(\+91|0)?[6-9]\d{9}$/.test(cleanPhone)) {
        error = 'Enter a valid 10-digit Indian phone number';
      }
    } else if (name === 'email') {
      if (!trimmed) {
        error = 'Email address is required';
      } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
        error = 'Enter a valid email address (e.g. name@domain.com)';
      }
    } else if (name === 'message') {
      if (!trimmed) {
        error = 'Requirements or site details are required';
      } else if (trimmed.length < 10) {
        error = 'Please provide at least 10 characters describing your requirement';
      }
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormStatus({ type: '', message: '' });

    if (touched[name]) {
      const errorMsg = validateField(name, value);
      setFormErrors((prev) => ({ ...prev, [name]: errorMsg }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errorMsg = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const errors = {
      name: validateField('name', formData.name),
      phone: validateField('phone', formData.phone),
      email: validateField('email', formData.email),
      message: validateField('message', formData.message),
    };

    setTouched({
      name: true,
      phone: true,
      email: true,
      message: true,
    });

    const hasErrors = Object.values(errors).some((err) => !!err);
    if (hasErrors) {
      setFormErrors(errors);
      setFormStatus({
        type: 'error',
        message: 'Please resolve the highlighted field errors before submitting.',
      });
      return;
    }

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setFormStatus({
      type: 'success',
      message: 'Thank you! Our advisory team will reach out to you within 24 hours.',
    });
    setFormData({ name: '', phone: '', email: '', service: '', message: '' });
    setFormErrors({});
    setTouched({});
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      label: 'Head Office',
      value: 'B-192, Sri Krishna Puri, Patna, Bihar 800001',
      badge: 'Headquarters'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      label: 'Direct Line',
      value: '+91 92348 04849',
      href: 'tel:9234804849',
      badge: '24/7 Helpline'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Official Email',
      value: 'arvihharinfo@gmail.com',
      href: 'mailto:arvihharinfo@gmail.com',
      badge: 'Inquiries'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Office Timings',
      value: 'Monday – Saturday: 9:00 AM – 7:00 PM',
      badge: 'Operational'
    },
  ];

  const services = [
    'Security Services (Armed & Unarmed)',
    'Facility Management & Housekeeping',
    'Manpower Outsourcing (ICT / Staffing)',
    'IT & Digitization Solutions',
    'Corporate Risk & Consultancy'
  ];

  return (
    <section id="contact" className="contact-section relative overflow-hidden">
      {/* Background Dot Grid Overlay */}
      <div 
        className="contact-bg-dots pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#1976D2 0.75px, transparent 0.75px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Decorative ambient glowing orbs */}
      <div className="contact-orb contact-orb-1" />
      <div className="contact-orb contact-orb-2" />

      <div className="contact-container relative z-10">

        {/* ===== HEADER (Minimal top space, matching stylish typography) ===== */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="contact-header"
        >
          <div className="contact-badge-pill">
            <span className="contact-badge-dot" />
            Get In Touch
          </div>
          <h2 className="contact-title">
            Let's Discuss Your{' '}
            <span className="contact-title-accent">Security & Facility Needs</span>
          </h2>
          <div className="contact-divider" />
          <p className="contact-subtitle">
            Our team is ready to evaluate your requirements and offer a customized, dependable solution.
          </p>
        </motion.div>

        {/* ===== SEAMLESS FLOATING 3D WORKSPACE ===== */}
        <div className="contact-floating-grid">

          {/* ===== LEFT: FLOATING CONTACT INFO TILES ===== */}
          <motion.div
            initial={{ opacity: 0, x: -25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="contact-info-col"
          >
            <div className="contact-intro-box">
              <span className="contact-kicker">Immediate Assistance</span>
              <h3 className="contact-info-heading">Direct Channels</h3>
              <p className="contact-info-desc">
                Have questions about deployments, rates, or staffing? Reach out directly through any of our certified channels.
              </p>
            </div>

            <div className="floating-info-stack">
              {contactInfo.map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4, scale: 1.015 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 20 }}
                  className="floating-info-card"
                >
                  <div className="info-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="info-text-area">
                    <div className="info-meta-row">
                      <span className="info-label">{item.label}</span>
                      <span className="info-tag">{item.badge}</span>
                    </div>
                    {item.href ? (
                      <a href={item.href} className="info-val info-link">
                        {item.value}
                      </a>
                    ) : (
                      <p className="info-val">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Quick Connect Floating Bar */}
            <div className="floating-quick-connect">
              <span className="quick-label">Fast Connect:</span>
              <div className="quick-action-group">
                <a 
                  href="https://wa.me/919234804849" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="quick-icon-btn whatsapp"
                  title="Chat on WhatsApp"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.698c.969.54 1.861.855 2.796.856 3.183 0 5.768-2.587 5.768-5.766 0-3.18-2.585-5.765-5.768-5.765zm3.385 8.163c-.145.409-.838.777-1.164.827-.327.05-1.173.308-3.08-1.442-1.907-1.75-2.029-2.584-2.122-2.827-.094-.242-.204-.617-.204-1.026 0-.409.215-.613.292-.716.077-.103.204-.153.308-.153.103 0 .204.004.292.008.093.004.218-.035.339.255.121.291.417 1.014.453 1.088.036.074.06.16.012.257-.048.097-.073.158-.145.243-.073.085-.154.19-.22.255-.073.073-.148.153-.064.297.085.145.378.623.81 1.008.558.497 1.028.65 1.173.722.145.073.23.061.315-.036.085-.097.364-.424.461-.57.097-.145.194-.121.327-.073.133.049.849.4 1.006.474.157.073.262.109.303.17.04.06.04.352-.105.761z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
                <a 
                  href="tel:9234804849" 
                  className="quick-icon-btn call"
                  title="Call Us Now"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Call Direct</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* ===== RIGHT: 3D FLOATING INPUTS FORM ===== */}
          <motion.div
            initial={{ opacity: 0, x: 25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="contact-form-col"
          >
            <form className="floating-form-stage" onSubmit={handleSubmit} noValidate>
              <div className="form-head-compact">
                <span className="contact-kicker">Quick Response Guaranteed</span>
                <h3 className="form-main-heading">Send Us a Direct Message</h3>
                <p className="form-sub-text">
                  Provide your requirement specifications below and get a verified proposal.
                </p>
              </div>

              {formStatus.type === 'success' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="floating-success-card"
                >
                  <span className="success-icon">✓</span>
                  <div>
                    <h5 className="font-bold text-[#1976D2]">Inquiry Received</h5>
                    <p className="text-sm text-[#001726]/80">{formStatus.message}</p>
                  </div>
                </motion.div>
              )}

              {formStatus.type === 'error' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="floating-error-card"
                >
                  <span className="error-status-icon">⚠️</span>
                  <div>
                    <h5 className="font-bold text-[#EF4444]">Attention Required</h5>
                    <p className="text-sm text-[#001726]/80">{formStatus.message}</p>
                  </div>
                </motion.div>
              )}

              {/* 3D Floating Fields Grid */}
              <div className="inputs-matrix">
                {/* Full Name */}
                <div className="floating-input-wrap">
                  <label className="floating-label">
                    Full Name <span className="req">*</span>
                  </label>
                  <div className={`field-container-3d ${touched.name && formErrors.name ? 'field-error' : ''}`}>
                    <span className="field-icon">👤</span>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. Rajesh Kumar"
                      required
                      className="field-3d"
                    />
                  </div>
                  {touched.name && formErrors.name && (
                    <span className="error-msg-inline">⚠️ {formErrors.name}</span>
                  )}
                </div>

                {/* Phone Number */}
                <div className="floating-input-wrap">
                  <label className="floating-label">
                    Phone Number <span className="req">*</span>
                  </label>
                  <div className={`field-container-3d ${touched.phone && formErrors.phone ? 'field-error' : ''}`}>
                    <span className="field-icon">📱</span>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 9876543210"
                      maxLength={14}
                      required
                      className="field-3d"
                    />
                  </div>
                  {touched.phone && formErrors.phone && (
                    <span className="error-msg-inline">⚠️ {formErrors.phone}</span>
                  )}
                </div>

                {/* Email Address */}
                <div className="floating-input-wrap">
                  <label className="floating-label">
                    Email Address <span className="req">*</span>
                  </label>
                  <div className={`field-container-3d ${touched.email && formErrors.email ? 'field-error' : ''}`}>
                    <span className="field-icon">✉️</span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. rajesh@company.com"
                      required
                      className="field-3d"
                    />
                  </div>
                  {touched.email && formErrors.email && (
                    <span className="error-msg-inline">⚠️ {formErrors.email}</span>
                  )}
                </div>

                {/* Service Requirement Dropdown */}
                <div className="floating-input-wrap">
                  <label className="floating-label">Service Requirement</label>
                  <div className="field-container-3d">
                    <span className="field-icon">🛡️</span>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="field-3d select-3d"
                    >
                      <option value="">Select Required Service</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Message / Description */}
              <div className="floating-input-wrap w-full mt-4">
                <label className="floating-label">
                  Your Requirements & Site Details <span className="req">*</span>
                </label>
                <div className={`field-container-3d textarea-container-3d ${touched.message && formErrors.message ? 'field-error' : ''}`}>
                  <span className="field-icon textarea-icon">📝</span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Describe your site location, guard shift requirements, housekeeping scope, or ICT staffing details..."
                    required
                    rows={4}
                    className="field-3d textarea-3d"
                  />
                </div>
                {touched.message && formErrors.message && (
                  <span className="error-msg-inline">⚠️ {formErrors.message}</span>
                )}
              </div>

              {/* Submit CTA */}
              <motion.button
                type="submit"
                className="floating-submit-btn-3d"
                disabled={isSubmitting}
                whileHover={{ scale: 1.015, y: -2 }}
                whileTap={{ scale: 0.985 }}
              >
                <span>{isSubmitting ? 'Verifying & Submitting...' : 'Send Inquiry Now'}</span>
                <span className="btn-arrow">→</span>
              </motion.button>
            </form>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Contact;